import { ConfirmationModal, IconAttention, Text, useModal } from '@procurify/ui'
import { truncate } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TermsMethodsKindEnum } from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import {
  type TermOrMethod,
  useMutateDeleteTermOrMethod,
} from '@webapp/modules/purchasing/features/delete-term-or-method/models'
import messages from './messages'

// Angular `_truncate(name, { length: 12 })` toast parity
const TOAST_NAME_TRUNCATE_LENGTH = 12

interface UseDeleteTermOrMethodPresenterOptions {
  onSuccess?: () => void
}

/**
 * Danger confirmation + DELETE for one term/method row. The programmatic
 * `ConfirmationModal` replaces the Angular hold-to-delete anti-pattern
 * (BHV-031); the row disappears via list refetch, not a local splice.
 */
export const useDeleteTermOrMethodPresenter = (
  kind: TermsMethodsKindEnum,
  options: UseDeleteTermOrMethodPresenterOptions = {}
) => {
  const intl = useProcurifyIntl()
  const modal = useModal()
  const { mutateAsync, isLoading } = useMutateDeleteTermOrMethod(kind)

  const deleteItem = (item: TermOrMethod) => {
    modal.showModal(ConfirmationModal, {
      open: true,
      icon: <IconAttention />,
      type: 'danger',
      header: intl.formatMessage(messages.confirmationModalTitle),
      content: (
        <Text>
          {intl.formatMessage(messages.confirmationModalDescription, {
            name: (
              <Text bold inline>
                {item.name}
              </Text>
            ),
          })}
        </Text>
      ),
      confirmButtonText: intl.formatMessage(
        messages.confirmationModalConfirmButton
      ),
      cancelButtonText: intl.formatMessage(
        messages.confirmationModalCancelButton
      ),
      handleDismiss: () => {
        // Cancel makes no API call (BHV-031)
        modal.hideModal()
      },
      onConfirm: async (done: () => void) => {
        try {
          await mutateAsync(item.id)

          toast.success({
            title: intl.formatMessage(messages.successTitle),
            children: intl.formatMessage(messages.successMessage, {
              name: truncate(item.name, {
                length: TOAST_NAME_TRUNCATE_LENGTH,
              }),
            }),
          })

          modal.hideModal()
          options.onSuccess?.()
        } catch (error) {
          const errorMessage =
            getMessageFromError(error) ||
            intl.formatMessage(messages.defaultErrorMessage)

          toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children: errorMessage,
          })

          // Keep the modal open/retryable on failure
          done()
        }
      },
    })
  }

  return {
    commands: {
      deleteItem,
    },
    states: {
      isDeleting: isLoading,
    },
  }
}
